import { z } from "zod";

// Shared user reference (requester, requestedFor, assignedTo)
const requestListUserSchema = z.object({
  userId: z.number(),
  userName: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string(),
  email: z.string(),
});

const requestListAccessSchema = z.object({
  accessId: z.number(),
  accessName: z.string(),
});

/** @description Zod schema for a single request list item. */
export const requestListItemSchema = z.object({
  requestId: z.number(),
  requestType: z.string(),
  requestStatus: z.string(),
  requester: requestListUserSchema,
  requestedFor: z.array(requestListUserSchema),
  accesses: z.array(requestListAccessSchema),
  assignedTo: z.array(requestListUserSchema),
  submittedOn: z.string(),
  expiresOn: z.string().nullable(),
});

export type RequestListItem = z.infer<typeof requestListItemSchema>;

/** @description Zod schema for the paginated request list API response. */
export const RequestListResponseSchema = z.object({
  data: z.array(requestListItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  totalCount: z.number(),
});

export type RequestListResponse = z.infer<typeof RequestListResponseSchema>;

// ── User list (for request-create user search) ────────────────────────────────

/** @description Zod schema for a single user item in user search results. */
export const userListItemSchema = z.object({
  id: z.number(),
  username: z.string(),
  email: z.string(),
});

export type UserListItem = z.infer<typeof userListItemSchema>;

/** @description Zod schema for the user list API response (flat array). */
export const UserListResponseSchema = z.array(userListItemSchema);

export type UserListResponse = z.infer<typeof UserListResponseSchema>;

// ── Platform list (for request-create platform selection) ─────────────────────

/** @description Zod schema for a single platform item in platform search results. */
export const platformListItemSchema = z.object({
  name: z.string(),
  platform: z.string(),
  agentCount: z.number(),
});

export type PlatformListItem = z.infer<typeof platformListItemSchema>;

/** @description Zod schema for the platform list API response (flat array). */
export const PlatformListResponseSchema = z.array(platformListItemSchema);

export type PlatformListResponse = z.infer<typeof PlatformListResponseSchema>;

// ── Agent list (for configure-agent step) ────────────────────────────────────

const agentAccessSchema = z.object({
  users: z.array(
    z.object({
      principalId: z.string(),
      accessType: z.string(),
    }),
  ),
  userCount: z.number(),
  usersTruncated: z.boolean(),
  groupCount: z.number(),
  agentCount: z.number(),
});

/** @description Zod schema for a single agent item in the configure-agent step. */
export const agentListItemSchema = z.object({
  agentId: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  description: z.string().nullable(),
  llmModel: z.string().nullable(),
  criticality: z.enum(["critical", "high", "medium", "low"]).nullable(),
  platform: z.string().nullable(),
  status: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  access: agentAccessSchema,
});

export type AgentListItem = z.infer<typeof agentListItemSchema>;

const agentListPaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  totalCount: z.number(),
  hasNextPage: z.boolean(),
});

/** @description Zod schema for the paginated agent list API response. */
export const AgentListResponseSchema = z.object({
  data: z.array(agentListItemSchema),
  pagination: agentListPaginationSchema,
});

export type AgentListResponse = z.infer<typeof AgentListResponseSchema>;
